import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HiOutlineArrowLeft,
  HiOutlineDocumentArrowDown,
  HiOutlineCalendar,
  HiOutlineClipboardDocumentList,
  HiOutlineExclamationTriangle,
  HiOutlineClock,
  HiOutlineCalendarDays,
  HiOutlineCheckCircle,
  HiOutlineChevronDown,
  HiOutlineChevronUp,
} from 'react-icons/hi2';
import plantService from '../services/plantService';
import lineService from '../services/lineService';
import machineService from '../services/machineService';
import pmStandardService from '../services/pmStandardService';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import { SEMANTIC_SUCCESS_MID } from '../theme/chartColors';
import {
  showReportGeneratingToast,
  reportGenerationDone,
  reportGenerationFailed,
} from '../utils/reportGenerationToast';
import './Reports.css';

const FREQUENCY_OPTIONS = ['All', 'Daily', 'Weekly', 'Monthly', 'Quarterly', 'Yearly'];
const STATUS_OPTIONS = ['All', 'Due', 'Completed', 'Overdue'];

const IndividualPmTaskReport = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [complianceData, setComplianceData] = useState([]);
  const [activeTab, setActiveTab] = useState('tasks');
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });
  const [frequency, setFrequency] = useState('All');
  const [status, setStatus] = useState('All');

  // Filter states
  const [plants, setPlants] = useState([]);
  const [allLines, setAllLines] = useState([]);
  const [allMachines, setAllMachines] = useState([]);
  
  const [selectedPlants, setSelectedPlants] = useState([]);
  const [selectedLines, setSelectedLines] = useState([]);
  const [selectedMachines, setSelectedMachines] = useState([]);
  const [error, setError] = useState('');

  const fetchData = async () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      setError('Please select both Start Date and End Date.');
      return;
    }
    setError('');
    showReportGeneratingToast();
    try {
      setLoading(true);
      const res = await pmStandardService.getIndividualPmTaskReport(
        dateRange.startDate,
        dateRange.endDate,
        frequency === 'All' ? null : frequency,
        status === 'All' ? null : status,
        selectedPlants,
        selectedLines,
        selectedMachines
      );
      setData(res?.tasks || []);
      setComplianceData(res?.compliance || []);
      reportGenerationDone('Report generated successfully!');
    } catch (err) {
      console.error('Error fetching individual task report:', err);
      setData([]);
      reportGenerationFailed('Failed to generate report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);



  const loadInitialData = async () => {
    try {
      const pData = await plantService.getAllPlants();
      setPlants(pData || []);
      
      const lData = await lineService.getAllLines();
      setAllLines(lData || []);
      
      const mData = await machineService.getAllMachines();
      setAllMachines(mData || []);

      // Auto-select plant if only 1 exists
      if (pData?.length === 1) {
        const pId = pData[0].plantId;
        setSelectedPlants([pId]);
      }

      // Auto-select all lines ONLY for single-plant users
      if (pData?.length === 1 && selectedLines.length === 0) {
        const pId = pData[0].plantId;
        const plantLines = (lData || []).filter(l => l.plantId === pId).map(l => l.lineId);
        if (plantLines.length > 0) {
          setSelectedLines(plantLines);
        }
      }
    } catch (err) {
      console.error('Error loading filter data:', err);
    }
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({ ...prev, [name]: value }));
  };

  // Cascading logic
  const lineOptions = selectedPlants.length > 0
    ? allLines.filter(l => selectedPlants.includes(l.plantId))
    : allLines;

  const machineOptions = selectedLines.length > 0
    ? allMachines.filter(m => selectedLines.includes(m.lineId))
    : selectedPlants.length > 0
      ? allMachines.filter(m => lineOptions.some(l => l.lineId === m.lineId))
      : allMachines;

  const handlePlantChange = (vals) => {
    setSelectedPlants(vals);
    const validLines = allLines.filter(l => vals.includes(l.plantId)).map(l => l.lineId);
    setSelectedLines(prev => prev.filter(id => validLines.includes(id)));
  };

  const handleLineChange = (vals) => {
    setSelectedLines(vals);
    const validMachines = allMachines.filter(m => vals.includes(m.lineId)).map(m => m.machineId);
    setSelectedMachines(prev => prev.filter(id => validMachines.includes(id)));
  };

  const exportToExcel = () => {
    const currentData = activeTab === 'tasks' ? data : complianceData;
    if (!Array.isArray(currentData) || currentData.length === 0) return;

    let worksheet;
    if (activeTab === 'tasks') {
      worksheet = XLSX.utils.json_to_sheet(
        currentData.map((item) => ({
          WHAT: `${item.standardCode} - ${item.standardName}`,
          WHERE: item.machineName,
          WHEN: item.frequency,
          WHO: item.doneBy || '-',
          WHY: item.why,
          HOW: item.how,
          DUE: item.dueDate ? new Date(item.dueDate).toLocaleDateString() : 'N/A',
          STATUS: item.status,
          SHUTDOWN: item.isMachineShutdownRequired ? 'YES' : 'No',
          TIME: item.minutesSpent ? `${item.minutesSpent}m` : '-',
        }))
      );
    } else {
      worksheet = XLSX.utils.json_to_sheet(
        currentData.map((item) => ({
          Plant: item.plantName,
          Line: item.lineName,
          WHERE: item.machineName,
          Scheduled: item.totalScheduled,
          'On-Time': item.completedOnTime,
          Overdue: item.overdue,
          'Open / Due Today': item.open || 0,
          'Compliance %': item.complianceRate,
        }))
      );
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, activeTab === 'tasks' ? 'Task Details' : 'Compliance Summary');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const dataBlob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });
    saveAs(dataBlob, `PM_Report_${activeTab}_${dateRange.startDate}_to_${dateRange.endDate}.xlsx`);
    toast.success('Report exported successfully!');
  };

  // Stat calculations
  const completedTasks = data.filter((d) => d.status === 'Completed');
  const totalMins = completedTasks.reduce((acc, curr) => acc + (curr.minutesSpent || 0), 0);
  const avgMins = completedTasks.length > 0 ? (totalMins / completedTasks.length).toFixed(1) : '0.0';

  const STAT_CARDS = [
    {
      key: 'overdue',
      label: 'Overdue',
      icon: HiOutlineExclamationTriangle,
      color: '#ef4444',
      borderColor: '#ef4444',
      sub: 'Past due date',
      count: data.filter((d) => {
        if (d.status === 'Completed' || d.status === 'Skipped') return false;
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const due = d.dueDate ? new Date(d.dueDate) : null;
        return d.status === 'Overdue' || (due && due < today);
      }).length,
    },
    {
      key: 'dueToday',
      label: 'Due Today',
      icon: HiOutlineClock,
      color: '#3b82f6',
      borderColor: '#3b82f6',
      sub: 'Needs action today',
      count: data.filter((d) => {
        const todayStr = new Date().toISOString().split('T')[0];
        const dueStr = d.dueDate ? new Date(d.dueDate).toISOString().split('T')[0] : '';
        return dueStr === todayStr && (d.status === 'Scheduled' || d.status === 'Due');
      }).length,
    },
    {
      key: 'upcoming',
      label: 'Upcoming',
      icon: HiOutlineCalendarDays,
      color: '#8b5cf6',
      borderColor: '#8b5cf6',
      sub: 'Scheduled ahead',
      count: data.filter((d) => {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const due = d.dueDate ? new Date(d.dueDate) : null;
        return (d.status === 'Due' || d.status === 'Scheduled') && due && due > today;
      }).length,
    },
    {
      key: 'completed',
      label: 'Completed',
      icon: HiOutlineCheckCircle,
      color: SEMANTIC_SUCCESS_MID,
      borderColor: SEMANTIC_SUCCESS_MID,
      sub: 'Done in period',
      count: completedTasks.length,
    },
    {
      key: 'avgmins',
      label: 'Avg Min / Task',
      icon: HiOutlineClock,
      color: '#509183',
      borderColor: '#509183',
      sub: 'Average time per task',
      count: loading ? '—' : avgMins,
      noClick: true,
    },
  ];

  const hasData = data.length > 0 || complianceData.length > 0;

  return (
    <div className="pagecontainer report-page">
      {/* ── Page Header ── */}
      <div className="page-header">
        <div className="oee-report-header-left">
          <button
            className="oee-report-back-btn"
            onClick={() => navigate('/home/reports')}
            title="Back to Reports"
          >
            <HiOutlineArrowLeft size={18} />
          </button>
          <div>
            <h1 className="oee-report-title">
              <HiOutlineClipboardDocumentList /> PM Tasks Report
            </h1>
            <p className="oee-report-subtitle">
              View and export detailed records of individual PM tasks
            </p>
          </div>
        </div>
        <div className="header-actions">
          <button
            className="oee-filter-toggle-btn"
            onClick={() => setShowFilters((p) => !p)}
          >
            <HiOutlineCalendar size={16} />
            Filters
            {showFilters ? <HiOutlineChevronUp size={13} /> : <HiOutlineChevronDown size={13} />}
          </button>
          <button
            className="btn-primary btn"
            onClick={exportToExcel}
            disabled={loading || !hasData}
            style={{ backgroundColor: '#509183', borderColor: '#407a6e', color: '#fff' }}
            title={!hasData ? 'Generate a report first' : 'Export to Excel'}
          >
            <HiOutlineDocumentArrowDown /> Export
          </button>
        </div>
      </div>

      {/* ── Filter Panel ── */}
      {showFilters && (
        <div className="reportbox searchfilterbox">
          <div className="report-filters">
            <div className="filter-group">
              <label>Start Date</label>
              <input
                type="date"
                name="startDate"
                value={dateRange.startDate}
                onChange={handleDateChange}
                className={`filter-input ${error && !dateRange.startDate ? 'input-error' : ''}`}
              />
            </div>
            <div className="filter-group">
              <label>End Date</label>
              <input
                type="date"
                name="endDate"
                value={dateRange.endDate}
                onChange={handleDateChange}
                className={`filter-input ${error && !dateRange.endDate ? 'input-error' : ''}`}
              />
            </div>
            <div className="filter-group">
              <label>Frequency</label>
              <select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                className="filter-input"
                style={{ appearance: 'auto' }}
              >
                {FREQUENCY_OPTIONS.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="filter-input"
                style={{ appearance: 'auto' }}
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Plant</label>
              <MultiSelectDropdown
                options={plants.map(p => ({ id: p.plantId, name: p.plantName }))}
                selectedValues={selectedPlants}
                onChange={handlePlantChange}
                placeholder="All Plants"
                displayKey="name"
                valueKey="id"
              />
            </div>
            <div className="filter-group">
              <label>LINE</label>
              <MultiSelectDropdown
                options={lineOptions.map(l => ({ id: l.lineId, name: l.lineName }))}
                selectedValues={selectedLines}
                onChange={handleLineChange}
                placeholder="All Lines"
                displayKey="name"
                valueKey="id"
              />
            </div>
            <div className="filter-group">
              <label>MACHINES</label>
              <MultiSelectDropdown
                options={machineOptions.map(m => ({ id: m.machineId, name: m.machineName }))}
                selectedValues={selectedMachines}
                onChange={setSelectedMachines}
                placeholder="All Machines"
                displayKey="name"
                valueKey="id"
              />
            </div>
          </div>

          <div className="report-filter-actions">
            {(selectedPlants.length > 0 || selectedLines.length > 0 || selectedMachines.length > 0) && (
              <button 
                className="oee-clear-filters-btn" 
                onClick={() => {
                  setSelectedPlants([]);
                  setSelectedLines([]);
                  setSelectedMachines([]);
                  setData([]);
                }}
              >
                ✕ Clear Filters
              </button>
            )}
            <button type="button" className="btn-primary" onClick={fetchData} disabled={loading}>
              <HiOutlineCalendar />
              Generate Report
            </button>
          </div>
          {error && <p style={{ color: '#ef4444', fontSize: '13px', marginTop: '12px', fontWeight: 600, textAlign: 'center' }}>{error}</p>}
        </div>
      )}

      {/* ── KPI Cards ── */}
      {hasData && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '16px',
            marginBottom: '24px',
          }}
        >
          {STAT_CARDS.map((card) => (
            <div
              key={card.key}
              onClick={() => {
                if (!card.noClick) {
                  setStatus(card.key === 'overdue' ? 'Overdue' : card.key === 'completed' ? 'Completed' : 'Due');
                  fetchData();
                }
              }}
              style={{
                background: '#fff',
                borderRadius: '12px',
                border: '1px solid #e2e8f0; border-color: var(--border-primary)',
                borderLeft: `4px solid ${card.borderColor}`,
                padding: '18px 20px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                cursor: card.noClick ? 'default' : 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
              onMouseEnter={(e) => {
                if (!card.noClick) {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.1)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.05)';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <div style={{ padding: '6px', background: `${card.borderColor}18`, borderRadius: '6px', color: card.borderColor }}>
                  <card.icon size={18} />
                </div>
                <p style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {card.label}
                </p>
              </div>
              <h2 style={{ margin: '0 0 4px 0', fontSize: '26px', fontWeight: 700, color: card.color }}>
                {loading ? '—' : card.count}
              </h2>
              <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8' }}>{card.sub}</p>
            </div>
          ))}
        </div>
      )}

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', borderBottom: '2px solid #f1f5f9', paddingBottom: '0' }}>
        <button
          onClick={() => setActiveTab('tasks')}
          style={{
            padding: '10px 22px',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'tasks' ? '3px solid #509183' : '3px solid transparent',
            color: activeTab === 'tasks' ? '#509183' : '#64748b',
            fontWeight: 700,
            fontSize: '14px',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
        >
          Task Details ({data.length})
        </button>
        <button
          onClick={() => setActiveTab('compliance')}
          style={{
            padding: '10px 22px',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'compliance' ? '3px solid #509183' : '3px solid transparent',
            color: activeTab === 'compliance' ? '#509183' : '#64748b',
            fontWeight: 700,
            fontSize: '14px',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
        >
          By Machine ({complianceData.length})
        </button>
      </div>

      {/* ── Table ── */}
      <div className="reports-table">
        {!dateRange.startDate || !dateRange.endDate ? (
          <div className="report-placeholder">
            <HiOutlineClipboardDocumentList className="placeholder-icon" />
            <h3>PM Tasks Report</h3>
            <p>Select a date range and click "Generate Report" to view data</p>
          </div>
        ) : loading ? (
          <div className="loading-container">Loading report data…</div>
        ) : activeTab === 'tasks' ? (
          <div className="report-table-wrapper">
            <table className="report-table">
              <thead>
                <tr>
                  <th>WHAT</th>
                  <th>WHERE</th>
                  <th>WHEN</th>
                  <th>WHO</th>
                  <th>WHY</th>
                  <th>HOW</th>
                  <th>DUE</th>
                  <th>STATUS</th>
                  <th>SHUTDOWN</th>
                  <th>TIME</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr key={i}>
                    <td>
                      <div><strong>{row.standardCode}</strong></div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{row.standardName}</div>
                    </td>
                    <td>{row.machineName}</td>
                    <td>
                      <span className="priority-tag medium">{row.frequency}</span>
                    </td>
                    <td>{row.doneBy || '-'}</td>
                    <td>
                      <div style={{ fontSize: '0.75rem', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.why}>
                        {row.why || '-'}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: '0.75rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.how}>
                        {row.how || '-'}
                      </div>
                    </td>
                    <td>{row.dueDate ? new Date(row.dueDate).toLocaleDateString() : '-'}</td>
                    <td>
                      <span className={`status-badge small ${row.status?.toLowerCase()}`}>{row.status}</span>
                    </td>
                    <td style={{ fontWeight: 700, color: row.isMachineShutdownRequired ? '#ef4444' : '#94a3b8' }}>
                      {row.isMachineShutdownRequired ? 'YES' : 'No'}
                    </td>
                    <td>{row.minutesSpent ? `${row.minutesSpent}m` : '-'}</td>
                  </tr>
                ))}
                {data.length === 0 && (
                  <tr>
                    <td colSpan="10" className="no-data">No records found for the selected period</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="report-table-wrapper">
            <table className="report-table">
              <thead>
                <tr>
                  <th>Machine / Location</th>
                  <th>Scheduled</th>
                  <th>On-Time</th>
                  <th>Overdue</th>
                  <th>Open / Due</th>
                  <th>Compliance %</th>
                </tr>
              </thead>
              <tbody>
                {complianceData.map((row, i) => (
                  <tr key={i}>
                    <td>
                      <div><strong>{row.machineName}</strong></div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{row.lineName} | {row.plantName}</div>
                    </td>
                    <td style={{ fontWeight: 600 }}>{row.totalScheduled}</td>
                    <td style={{ color: SEMANTIC_SUCCESS_MID, fontWeight: 600 }}>{row.completedOnTime}</td>
                    <td style={{ color: '#ef4444', fontWeight: 600 }}>{row.overdue}</td>
                    <td style={{ color: '#3b82f6', fontWeight: 600 }}>{row.open || 0}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <strong style={{ minWidth: '40px' }}>{row.complianceRate}%</strong>
                        <div style={{ flex: 1, height: '6px', background: '#f1f5f9', borderRadius: '3px', minWidth: '60px' }}>
                          <div
                            style={{
                              width: `${row.complianceRate}%`,
                              height: '100%',
                              background: row.complianceRate >= 90 ? SEMANTIC_SUCCESS_MID : row.complianceRate >= 70 ? '#f59e0b' : '#ef4444',
                              borderRadius: '3px',
                            }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
                {complianceData.length === 0 && (
                  <tr>
                    <td colSpan="5" className="no-data">No compliance summary available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default IndividualPmTaskReport;
